import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  CircularProgress,
  Popover,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Edit, EllipsisVertical, Plus, Trash } from 'lucide-react';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { CalendarToday } from '@mui/icons-material';
import NestedActionSelect from './NestedActionSelect';

const childHeaders = [
  'Date & Time',
  'Delivery Reference',
  'Delivered Qty',
  'Quantity',
  'Action',
  'Comment',
  ''
];

const ChildTable = ({
  data,
  row,
  current,
  laydownList,
  loading,
  editIndex,
  setEditIndex,
  editEntry,
  setEditEntry,
  newEntries,
  setNewEntries,
  onUpdate,
  onDelete,
  onSaveAll,
  onCancel,
  onAddEntry,
  currentQty
}) => {
  const [dropdownAnchor, setDropdownAnchor] = useState(null);
  const [dropdownIndex, setDropdownIndex] = useState(null);

  const handleChange = (entryId, field, value) => {
    setNewEntries(prev => 
      prev.map(entry => 
        entry.id === entryId 
          ? { ...entry, [field]: value }
          : entry
      )
    );
  };

  const handleEditChange = (field, value) => {
    setEditEntry(prev => ({ ...prev, [field]: value }));
  };

  const handleRemoveEntry = (entryId) => {
    setNewEntries(prev => prev.filter(entry => entry.id !== entryId));
  };

  const handleEdit = (idx) => {
    if (newEntries.length > 0) {
      setNewEntries([]);
    }
    
    setEditIndex(idx);
    setEditEntry({ 
      ...data.Inventory_logs[idx], 
      time: dayjs(data.Inventory_logs[idx].time), 
      issued_quantity: data.Inventory_logs[idx].issued_quantity 
    });
    closeDropdown();
  };

  useEffect(() => {
    if (editIndex !== null || newEntries.length > 0) {
      closeDropdown();
    }
  }, [editIndex, newEntries.length]);

  const handleAddNewEntry = () => {
    if (editIndex !== null) {
      setEditIndex(null);
      setEditEntry(null);
    }
    
    setNewEntries([]);
    
    onAddEntry();
  };

  const openDropdown = (e, idx) => {
    setDropdownAnchor(e.currentTarget);
    setDropdownIndex(idx);
  };

  const closeDropdown = () => {
    setDropdownAnchor(null);
    setDropdownIndex(null);
  };

  const getValue = (values) => {
    switch (values?.purpose_code) {
      case 0:
        return 'Delivery';
      case 4:
        return 'Stock adjustment';
      case 2:
        return `Intersite movement issued to ${
          laydownList.find((item) => item.id === values.destination_laydown_id)?.name
        }`;
      case 3:
        return `Intersite movement received from ${
          laydownList.find((item) => item.id === values.destination_laydown_id)?.name
        }`;
      default:
        return '-';
    }
  };

  return (
    <Box m={1}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {childHeaders.map((h, i) => (
              <TableCell key={i} sx={{ bgcolor: '#fecaca', fontWeight: 600 }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Existing inventory logs */}
          {(data.Inventory_logs || []).map((item, idx) => (
            editIndex === idx ? (
              <TableRow key={idx}>
                <TableCell sx={{ minWidth: 210 }}>
                  <DateTimePicker
                    sx={{ maxWidth: "180px"}}
                    value={editEntry.time}
                    onChange={(v) => handleEditChange("time", v)}
                    slots={{
                      openPickerIcon: () => (
                        <CalendarToday sx={{ fontSize: 16 }} />
                      ),
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        InputProps: {
                          sx: { fontSize: 16 },
                        },
                        InputLabelProps: {
                          sx: { fontSize: 16 },
                        },
                      },
                    }}
                  />
                </TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <TextField
                    type="number"
                    size="small"
                    value={editEntry.issued_quantity}
                    onChange={(e) => handleEditChange('issued_quantity', e.target.value)}
                    InputProps={{
                      sx: {
                        height: 45,
                        fontSize: 16,
                        '& input': {
                          padding: '10px 14px',
                        },
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ 
                  minWidth: 200, 
                  maxWidth: 250, 
                  width: 250,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  <NestedActionSelect
                    value={editEntry.purpose_code}
                    onChange={(value) => handleEditChange('purpose_code', value)}
                    laydownList={laydownList}
                    currentLaydownId={current?.id}
                    sx={{ minWidth: "200px", maxWidth: "250px", width: "100%" }}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>
                  <TextField
                    type="text"
                    size="small"
                    value={editEntry.comments}
                    onChange={(e) => handleEditChange('comments', e.target.value)}
                    InputProps={{
                      sx: {
                        height: 45,
                        fontSize: 16,
                        '& input': {
                          padding: '10px 14px',
                        },
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: 70, maxWidth: 150 }}>
                  <Button size="small" onClick={onUpdate} disabled={loading}>
                    {loading ? <CircularProgress size={16} /> : 'Update'}
                  </Button>
                  <Button size="small" onClick={onCancel}>Cancel</Button>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow key={idx}>
                <TableCell>{item.time}</TableCell>
                <TableCell sx={{ height: "55px"}}>{item.delivery_reference || '-'}</TableCell>
                <TableCell sx={{ height: "55px"}}>{item?.purpose_code === 0 ? item.received_quantity : '-'}</TableCell>
                <TableCell sx={{ minWidth: 120, height: "55px" }}>{item.issued_quantity || (item?.purpose_code !== 0 && item.received_quantity) || '-'}</TableCell>
                <TableCell sx={{ height: "55px", minWidth: 200, maxWidth: 250, width: 250, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                    <Tooltip 
                        title={getValue(item)} 
                        arrow
                        componentsProps={{
                            tooltip: {
                            sx: {
                                backgroundColor: 'white',
                                color: 'black',
                                fontSize: 14,
                                border: '1px solid #ccc',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                maxWidth: 300,
                                padding: '8px 12px',
                            }
                            },
                            arrow: {
                            sx: {
                                color: 'white',
                                '&:before': {
                                border: '1px solid #ccc',
                                backgroundColor: 'white',
                                }
                            }
                            }
                        }}
                        placement="top"
                        >
                        {getValue(item)}
                    </Tooltip>
                </TableCell>
                <TableCell sx={{ minWidth: 150, height: "55px" }}>{item.comments}</TableCell>
                <TableCell sx={{ width: 70, maxWidth: 150, height: "55px" }}>
                    {item?.purpose_code === 0 ? "" :
                    <Box>
                        <IconButton size="small" onClick={(e) => openDropdown(e, idx)}>
                            <EllipsisVertical size={16} />
                        </IconButton>
                        <Popover
                            open={dropdownIndex === idx}
                            anchorEl={dropdownAnchor}
                            onClose={closeDropdown}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            PaperProps={{
                            sx: {
                                minWidth: 150,
                                width: 150,
                            }
                            }}
                        >
                            <Box p={1} sx={{ display: "flex", flexDirection: "column", width: '100%' }}>
                            <Button 
                                sx={{ 
                                justifyContent: "flex-start",
                                textAlign: "left",
                                width: '100%',
                                fontSize: 16,
                                py: 1,
                                '& .MuiButton-startIcon': {
                                    marginRight: 1,
                                    '& svg': {
                                    fontSize: 16
                                    }
                                }
                                }} 
                                size="medium" 
                                startIcon={<Edit size={18} />} 
                                onClick={() => handleEdit(idx)}
                                disabled={newEntries.length > 0 || (editIndex !== null && editIndex !== idx)}
                            >
                                Edit
                            </Button>
                            <Button 
                                sx={{ 
                                justifyContent: "flex-start",
                                textAlign: "left",
                                width: '100%',
                                fontSize: 16,
                                py: 1,
                                '& .MuiButton-startIcon': {
                                    marginRight: 1,
                                    '& svg': {
                                    fontSize: 16
                                    }
                                }
                                }} 
                                size="medium" 
                                startIcon={<Trash size={18} />} 
                                onClick={() => onDelete(item)} 
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={16} /> : 'Delete'}
                            </Button>
                            </Box>
                        </Popover>
                    </Box>
                 }
                </TableCell>
              </TableRow>
            )
          ))}
          
          {/* Multiple new entries */}
          {newEntries.map((entry, entryIndex) => (
            <TableRow key={entry.id}>
              <TableCell sx={{ minWidth: 210 }}>
                <DateTimePicker
                  sx={{ maxWidth: "180px"}}
                  value={entry.time}
                  onChange={(v) => handleChange(entry.id, "time", v)}
                  slots={{
                    openPickerIcon: () => (
                      <CalendarToday sx={{ fontSize: 16 }} />
                    ),
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      InputProps: {
                        sx: { fontSize: 16 },
                      },
                      InputLabelProps: {
                        sx: { fontSize: 16 },
                      },
                    },
                  }}
                />
              </TableCell>
              <TableCell sx={{ minWidth: 135 }}>-</TableCell>
              <TableCell>-</TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                <TextField
                  type="number"
                  size="small"
                  value={entry.issued_quantity}
                  onChange={(e) => handleChange(entry.id, 'issued_quantity', e.target.value)}
                  InputProps={{
                    sx: {
                      height: 45,
                      fontSize: 16,
                      '& input': {
                        padding: '10px 14px',
                      },
                    },
                  }}
                />
              </TableCell>
              <TableCell sx={{ 
                minWidth: 200, 
                maxWidth: 250, 
                width: 250,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                <NestedActionSelect
                  value={entry.purpose_code}
                  onChange={(value) => handleChange(entry.id, 'purpose_code', value)}
                  laydownList={laydownList}
                  currentLaydownId={current?.id}
                  sx={{ minWidth: "200px", maxWidth: "250px", width: "100%" }}
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ minWidth: 150 }}>
                <TextField
                  type="text"
                  size="small"
                  value={entry.comments}
                  onChange={(e) => handleChange(entry.id, 'comments', e.target.value)}
                  InputProps={{
                    sx: {
                      height: 45,
                      fontSize: 16,
                      '& input': {
                        padding: '10px 14px',
                      },
                    },
                  }}
                />
              </TableCell>
              <TableCell sx={{ width: 70, maxWidth: 150 }}>
                <IconButton 
                  size="small" 
                  onClick={() => handleRemoveEntry(entry.id)}
                  color="error"
                >
                  <Trash size={16} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      <Box mt={1} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography fontSize={16} ml={2}>
            Current Quantity: <b>{currentQty}</b>
          </Typography>
          {(editIndex !== null || newEntries.length > 0) && (
            <Typography 
              fontSize={14} 
              sx={{ 
                color: 'primary.main', 
                fontWeight: 600,
                backgroundColor: 'primary.50',
                px: 1,
                py: 0.5,
                borderRadius: 1
              }}
            >
              {editIndex !== null ? '✏️ Editing mode' : '➕ Adding new entry'}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button 
            startIcon={<Plus size={16} />} 
            onClick={handleAddNewEntry}
            variant="outlined"
            size="small"
            disabled={editIndex !== null || newEntries.length > 0}
          >
            Add Entry
          </Button>
          {newEntries.length > 0 && (
            <>
              <Button 
                onClick={onSaveAll}
                variant="contained"
                size="small"
                disabled={loading}
                sx={{ minWidth: 100 }}
              >
                {loading ? <CircularProgress size={16} /> : 'Save'}
              </Button>
              <Button 
                onClick={onCancel}
                variant="outlined"
                size="small"
                color="error"
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChildTable;